import { ccHbcCountries, phaseMeta } from "@/data/buOwnerData";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";

const phaseOrder = ["saturate", "scale", "pilot", "onboarding"];

export const AdoptionMap = () => {
  const { t } = useLang();
  const grouped = phaseOrder.map((p) => ({
    phase: p,
    meta: phaseMeta[p],
    countries: ccHbcCountries.filter((c) => c.phase === p),
  }));

  const activeBus = ccHbcCountries.filter((c) => c.outlets > 0).length;
  const totalLive = ccHbcCountries
    .filter((c) => c.phase === "saturate" || c.phase === "scale" || c.phase === "pilot")
    .reduce((s, c) => s + c.outlets, 0);

  return (
    <div className="mb-14" data-testid="adoption-map-section">
      <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
        <span className="cchbc-eyebrow mb-3">{t("CC HBC Network — FORA Adoption")}</span>
        <h2 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-3">
          {t("Who's already in")}
        </h2>
        <p className="text-sm text-[#555555] mt-2 max-w-2xl leading-relaxed">
          {t("Live across")} <strong className="text-[#000000]">{totalLive.toLocaleString()}</strong> {t("outlets in")} <strong className="text-[#000000]">{activeBus}</strong> {t("Coca-Cola HBC business units. Each country has a dedicated CC HBC owner driving the FORA programme locally.")}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {grouped.map(({ phase, meta, countries }) => (
          <div
            key={phase}
            className="bg-white border border-[#e8e8e8] rounded-sm p-4"
            data-testid={`adoption-column-${phase}`}
          >
            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-[#e8e8e8]">
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: meta.color }}
              />
              <p className="font-condensed text-base font-black uppercase tracking-tight text-[#000000]">
                {t(meta.label)}
              </p>
              <span className="text-[10px] text-[#555555] font-bold ml-auto">{countries.length}</span>
            </div>
            <div className="space-y-1.5">
              {countries.length === 0 ? (
                <p className="text-[11px] text-[#555555] italic">—</p>
              ) : (
                countries.map((c) => (
                  <div
                    key={c.code}
                    className="py-1.5 hover:bg-[#fafafa] rounded-sm transition-colors px-1.5"
                    data-testid={`adoption-country-${c.code}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-base">{c.flag}</span>
                      <span className="text-xs font-semibold text-[#000000] flex-1 truncate">{t(c.name)}</span>
                      {c.outlets > 0 && (
                        <span className="text-[10px] font-bold text-[#dd260e] tabular-nums shrink-0">
                          {c.outlets.toLocaleString()}
                        </span>
                      )}
                    </div>
                    {c.buOwner && c.buOwner !== "—" && (
                      <p className="text-[9px] text-[#555555] mt-0.5 pl-7 truncate" title={c.buOwner}>{c.buOwner}</p>
                    )}
                    {c.note && (
                      <p className="text-[9px] text-[#dd260e] italic mt-0.5 pl-7 truncate" title={c.note}>{t(c.note)}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-[#555555] italic mt-4 flex items-center gap-2">
        <ArrowUpRight className="w-3 h-3 text-[#dd260e]" />
        {t("Indicative figures — replace with live CC HBC operations data before publication.")}
      </p>
    </div>
  );
};
